/**
 * Composable for branding-related utilities
 * Returns a per-account brand payload overlaid on the installation-wide
 * globalConfig defaults. The current account's `settings.branding` (if
 * present) wins; otherwise the installation values are used.
 */
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';

const accountBrandingFor = (account, globalConfig) => {
  const accountBranding = account?.settings?.branding || {};
  return {
    name: accountBranding.name || globalConfig?.installationName,
    brandName: accountBranding.name || globalConfig?.brandName,
    logo: accountBranding.logo_url || globalConfig?.logo,
    logoDark: accountBranding.logo_dark_url || globalConfig?.logoDark,
    logoThumbnail:
      accountBranding.logo_thumbnail_url || globalConfig?.logoThumbnail,
    brandUrl: accountBranding.brand_url || globalConfig?.brandUrl,
    widgetBrandURL:
      accountBranding.widget_brand_url || globalConfig?.widgetBrandURL,
    primaryColor: accountBranding.primary_color || null,
  };
};

export function useBranding() {
  const globalConfig = useMapGetter('globalConfig/get');
  const currentAccount = useMapGetter('getCurrentAccount');

  const branding = computed(() =>
    accountBrandingFor(currentAccount.value, globalConfig.value)
  );

  /**
   * Replaces "Chatwoot" (any casing) in text with the brand name for the
   * current account, falling back to the installation name.
   * @param {string} text - The text to process
   * @returns {string} - Text with "Chatwoot" replaced by the brand name
   */
  const replaceInstallationName = text => {
    if (!text) return text;

    const installationName = branding.value.name;
    if (!installationName) return text;

    return text.replace(/chatwoot/gi, installationName);
  };

  return {
    branding,
    replaceInstallationName,
  };
}
